import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import ViewSidebarRounded from '@mui/icons-material/ViewSidebarRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { ModeContext } from '../../theme/ThemeProvider';
import { fetchTenantSettings, saveTenantSettings, getTenantIdFromStorage } from '../../services/tenantSettingsService';
import SettingsPageHeader from './components/SettingsPageHeader';
import StickySaveBar from './components/StickySaveBar';
import { SelectMenu } from '../../ui-kit';
import { useNavigation } from '../../ui/BootstrapProvider';
import { homeChoices, homeId } from '../../navigation/home';

type SidebarPosition = 'left' | 'right';

const SUPPORTED_CURRENCIES: Array<{ code: string; label: string; symbol: string }> = [
  { code: 'USD', label: 'US Dollar', symbol: '$' },
  { code: 'EUR', label: 'Euro', symbol: '€' },
  { code: 'GBP', label: 'British Pound', symbol: '£' },
  { code: 'INR', label: 'Indian Rupee', symbol: '₹' },
  { code: 'JPY', label: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', label: 'Chinese Yuan', symbol: '¥' },
  { code: 'AUD', label: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', label: 'Canadian Dollar', symbol: 'C$' },
  { code: 'CHF', label: 'Swiss Franc', symbol: 'CHF' },
];

const SUPPORTED_CALENDARS: Array<{ code: string; label: string }> = [
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'AU', label: 'Australia' },
  { code: 'BR', label: 'Brazil' },
  { code: 'CA', label: 'Canada' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'IN', label: 'India' },
  { code: 'JP', label: 'Japan' },
  { code: 'MX', label: 'Mexico' },
  { code: 'SG', label: 'Singapore' },
  { code: 'US', label: 'United States' },
  { code: 'ZA', label: 'South Africa' },
];

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}

function SectionHeader({ icon, title, subtitle }: SectionHeaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box
        sx={(t) => ({
          width: 32,
          height: 32,
          borderRadius: `${t.ds.radius.sm}px`,
          bgcolor: t.tokens.brand.primarySoft,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        })}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45, mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export default function WorkspaceSettingsPage() {
  const { notify } = React.useContext(SnackbarContext);
  const { refreshBranding } = React.useContext(ModeContext);
  const tenantId = React.useMemo(() => getTenantIdFromStorage(), []);

  const [sidebarPosition, setSidebarPosition] = React.useState<SidebarPosition>('left');
  // A page from this workspace's own sidebar, by its id (navigation/home.ts).
  const [defaultHomePage, setDefaultHomePage] = React.useState<string>('copilot');
  const navigation = useNavigation();
  const homeOptions = React.useMemo(() => homeChoices(navigation), [navigation]);
  const [holidayCalendars, setHolidayCalendars] = React.useState<string[]>([]);
  const [currencyCode, setCurrencyCode] = React.useState<string>('USD');

  const [loading, setLoading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);

  const hydrate = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const s = await fetchTenantSettings(tenantId);
      const branding = (s['BRANDING'] || {}) as Record<string, any>;
      if (branding.sidebarPosition === 'left' || branding.sidebarPosition === 'right') {
        setSidebarPosition(branding.sidebarPosition);
      }
      // The old settings saved one of three words; they are read as the
      // sidebar pages they meant.
      if (typeof branding.defaultHomePage === 'string') {
        setDefaultHomePage(homeId(branding.defaultHomePage));
      }
      const forecast = s['FORECAST'];
      if (forecast && Array.isArray(forecast.holiday_calendars)) {
        setHolidayCalendars(forecast.holiday_calendars as string[]);
      }
      const currency = (s['CURRENCY'] || {}) as Record<string, any>;
      if (typeof currency.code === 'string') {
        setCurrencyCode(currency.code.toUpperCase());
      }
    } catch (e: any) {
      notify(e?.message || 'Failed to load workspace settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, notify]);

  React.useEffect(() => { hydrate(); }, [hydrate]);

  const save = async () => {
    if (!tenantId) {
      notify('Missing tenant', 'error');
      return;
    }
    setSaving(true);
    try {
      // BRANDING is shared with the Branding page — merge into the existing
      // sub-object so we don't overwrite colors/logo on save.
      await saveTenantSettings(tenantId, (current) => ({
        BRANDING: {
          ...((current.BRANDING as Record<string, any>) || {}),
          sidebarPosition,
          defaultHomePage,
        },
        FORECAST: {
          ...((current.FORECAST as Record<string, any>) || {}),
          holiday_calendars: holidayCalendars,
        },
        CURRENCY: {
          ...((current.CURRENCY as Record<string, any>) || {}),
          code: currencyCode,
        },
      }));
      notify('Workspace settings saved', 'success');
      // Sidebar position and currency both come from the shared bootstrap
      // payload — refresh it so the layout and amounts reflect changes
      // immediately, without a full reload.
      refreshBranding();
    } catch (e: any) {
      notify(e?.message || 'Failed to save workspace settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cardSx = { mb: 3 };
  const cardContentSx = {
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 3 },
    '&:last-child': { pb: { xs: 2, sm: 3 } },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <SettingsPageHeader description="Defaults that shape how everyone in your tenant uses the product — navigation, Copilot governance, and forecasting." />

      {/* Navigation */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<ViewSidebarRounded sx={{ fontSize: 18 }} />}
            title="Navigation"
            subtitle="Where the sidebar lives and which page users land on after login."
          />
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                Sidebar position
              </Typography>
              <ToggleButtonGroup
                value={sidebarPosition}
                exclusive
                onChange={(_e, val: SidebarPosition | null) => {
                  if (val) setSidebarPosition(val);
                }}
                size="small"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <ToggleButton value="left" sx={{ px: 3, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                  Left
                </ToggleButton>
                <ToggleButton value="right" sx={{ px: 3, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                  Right
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HomeRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
                <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>
                  Default home page
                </Typography>
              </Box>
              {/* Plain spoken English (manual-dashboard/CLAUDE.md, "Writing"). */}
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, mb: 1.25 })}>
                The page we open for everyone in this workspace when they sign in, and when they click the logo.
                You can pick any page from this workspace&apos;s sidebar, apart from the ones only admins can open.
              </Typography>
              <SelectMenu
                label="Home page"
                value={defaultHomePage}
                options={homeOptions}
                onChange={setDefaultHomePage}
                size="medium"
                align="start"
              />
              {homeOptions.length > 0 && !homeOptions.some((o) => o.value === defaultHomePage) && (
                <Typography variant="caption" sx={(t) => ({ display: 'block', mt: 0.75, color: t.tokens.semantic.warning })}>
                  The page saved as the home page is not in this workspace&apos;s sidebar any more, so we open Copilot
                  instead. Pick another page to change it.
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Forecasting */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<TrendingUpRounded sx={{ fontSize: 18 }} />}
            title="Forecasting"
            subtitle="Holiday calendars used to enrich forecasts with public-holiday effects. Pick all countries where your business operates."
          />
          <FormControl fullWidth>
            <InputLabel id="holiday-calendars-label">Holiday calendars</InputLabel>
            <Select
              labelId="holiday-calendars-label"
              label="Holiday calendars"
              multiple
              value={holidayCalendars}
              onChange={(e: { target: { value: string | string[] } }) => {
                const val = e.target.value;
                setHolidayCalendars(typeof val === 'string' ? val.split(',') : val);
              }}
              renderValue={(selected: unknown) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((code) => {
                    const cal = SUPPORTED_CALENDARS.find((c) => c.code === code);
                    return (
                      <Chip
                        key={code}
                        label={cal ? `${cal.label} (${code})` : code}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {SUPPORTED_CALENDARS.map((cal) => (
                <MenuItem key={cal.code} value={cal.code}>
                  {cal.label} ({cal.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Regional */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<PaymentsRounded sx={{ fontSize: 18 }} />}
            title="Regional"
            subtitle="Currency used for all amounts shown across the app."
          />
          <FormControl fullWidth>
            <InputLabel id="currency-label">Currency</InputLabel>
            <Select
              labelId="currency-label"
              label="Currency"
              value={currencyCode}
              onChange={(e: { target: { value: string } }) => setCurrencyCode(e.target.value)}
            >
              {SUPPORTED_CURRENCIES.map((cur) => (
                <MenuItem key={cur.code} value={cur.code}>
                  {cur.symbol} {cur.label} ({cur.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <StickySaveBar
        description="Sidebar, home-page, and currency changes apply immediately on save."
        onSave={save}
        onReset={hydrate}
        saving={saving}
        disabled={loading}
      />
    </Box>
  );
}
